import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { renderToString } from 'react-dom/server';
import {
  MapPin,
  Loader2,
  CalendarDays,
  DollarSign,
  Navigation,
  Palette,
  Music,
  Zap,
  Frame,
  Home,
  Tag,
  Leaf,
  Gift,
  Calendar,
  ChevronRight,
  Sparkles,
  Eye,
  EyeOff,
  Clock,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/button';
import { EVENT_TYPES, formatDate, formatCurrency, normalizeState } from '../../lib/utils';
import { geocodeAPI, eventsAPI, favoritesAPI, eventScoutAPI } from '../../lib/api';
import { MapSidePanel } from './MapSidePanel';
import { toast } from 'sonner';

const TYPE_ICON_NAME_TO_COMPONENT = {
  Palette,
  Music,
  Zap,
  Frame,
  Home,
  Tag,
  Leaf,
  Gift,
  Calendar,
};

const iconFor = (typeKey) => {
  const iconName = EVENT_TYPES[typeKey]?.icon || 'Calendar';
  return TYPE_ICON_NAME_TO_COMPONENT[iconName] || Calendar;
};

const labelFor = (typeKey) => EVENT_TYPES[typeKey]?.label || EVENT_TYPES.other.label;

const colorFor = (typeKey) => EVENT_TYPES[typeKey]?.color_hex || EVENT_TYPES.other.color_hex;

// USA default bounds (continental US)
const USA_DEFAULT_BOUNDS = [
  [24.396308, -125.0],
  [49.384358, -66.93457],
];

const USA_CENTER = [39.8283, -98.5795];

// Zoom level at/above which pins show always-visible name + venue labels
const STREET_LABEL_ZOOM = 13;

const STATE_COORDS = {
  Alabama: { lat: 32.806671, lng: -86.79113 },
  Alaska: { lat: 61.370716, lng: -152.404419 },
  Arizona: { lat: 33.729759, lng: -111.431221 },
  Arkansas: { lat: 34.969704, lng: -92.373123 },
  California: { lat: 36.116203, lng: -119.681564 },
  Colorado: { lat: 39.059811, lng: -105.311104 },
  Connecticut: { lat: 41.597782, lng: -72.755371 },
  Delaware: { lat: 39.318523, lng: -75.507141 },
  Florida: { lat: 27.766279, lng: -81.686783 },
  Georgia: { lat: 33.040619, lng: -83.643074 },
  Hawaii: { lat: 21.094318, lng: -157.498337 },
  Idaho: { lat: 44.240459, lng: -114.478828 },
  Illinois: { lat: 40.349457, lng: -88.986137 },
  Indiana: { lat: 39.849426, lng: -86.258278 },
  Iowa: { lat: 42.011539, lng: -93.210526 },
  Kansas: { lat: 38.5266, lng: -96.726486 },
  Kentucky: { lat: 37.66814, lng: -84.670067 },
  Louisiana: { lat: 31.169546, lng: -91.867805 },
  Maine: { lat: 44.693947, lng: -69.381927 },
  Maryland: { lat: 39.063946, lng: -76.802101 },
  Massachusetts: { lat: 42.230171, lng: -71.530106 },
  Michigan: { lat: 43.326618, lng: -84.536095 },
  Minnesota: { lat: 45.694454, lng: -93.900192 },
  Mississippi: { lat: 32.741646, lng: -89.678696 },
  Missouri: { lat: 38.456085, lng: -92.288368 },
  Montana: { lat: 46.921925, lng: -110.454353 },
  Nebraska: { lat: 41.12537, lng: -98.268082 },
  Nevada: { lat: 38.313515, lng: -117.055374 },
  'New Hampshire': { lat: 43.452492, lng: -71.563896 },
  'New Jersey': { lat: 40.298904, lng: -74.521011 },
  'New Mexico': { lat: 34.840515, lng: -106.248482 },
  'New York': { lat: 42.165726, lng: -74.948051 },
  'North Carolina': { lat: 35.630066, lng: -79.806419 },
  'North Dakota': { lat: 47.528912, lng: -99.784012 },
  Ohio: { lat: 40.388783, lng: -82.764915 },
  Oklahoma: { lat: 35.565342, lng: -96.928917 },
  Oregon: { lat: 44.572021, lng: -122.070938 },
  Pennsylvania: { lat: 40.590752, lng: -77.209755 },
  'Rhode Island': { lat: 41.680893, lng: -71.51178 },
  'South Carolina': { lat: 33.856892, lng: -80.945007 },
  'South Dakota': { lat: 44.299782, lng: -99.438828 },
  Tennessee: { lat: 35.747845, lng: -86.692345 },
  Texas: { lat: 31.054487, lng: -97.563461 },
  Utah: { lat: 40.150032, lng: -111.862434 },
  Vermont: { lat: 44.045876, lng: -72.710686 },
  Virginia: { lat: 37.769337, lng: -78.169968 },
  Washington: { lat: 47.400902, lng: -121.490494 },
  'West Virginia': { lat: 38.491226, lng: -80.954453 },
  Wisconsin: { lat: 44.268543, lng: -89.616508 },
  Wyoming: { lat: 42.755966, lng: -107.30249 },
  'District of Columbia': { lat: 38.9072, lng: -77.0369 },
};

const getStateCoords = (state) => {
  if (!state) return null;
  const normalized = normalizeState(state);
  return STATE_COORDS[normalized] || null;
};

// ----- Component to track bounds + zoom changes -----
const BoundsTracker = ({ onChange, onUserDrag, useMapEventsHook }) => {
  useMapEventsHook({
    moveend: (e) => onChange(e.target.getBounds(), e.target.getZoom()),
    zoomend: (e) => onChange(e.target.getBounds(), e.target.getZoom()),
    load: (e) => onChange(e.target.getBounds(), e.target.getZoom()),
    dragstart: () => onUserDrag?.(),
  });
  return null;
};

// ----- Initial view: fit to all pins once geocoded (fallback: home + radius) -----
const InitialViewController = ({ homeBounds, pinsBounds, hasPins, useMapHook, userDraggedRef }) => {
  const map = useMapHook();
  const homeAppliedRef = useRef(false);
  const pinsAppliedRef = useRef(false);
  useEffect(() => {
    if (pinsAppliedRef.current) return;
    if (hasPins && pinsBounds && !userDraggedRef.current) {
      pinsAppliedRef.current = true;
      map.fitBounds(pinsBounds, { maxZoom: 11 });
    } else if (homeBounds && !homeAppliedRef.current) {
      homeAppliedRef.current = true;
      map.fitBounds(homeBounds);
    }
  }, [homeBounds, pinsBounds, hasPins, map, userDraggedRef]);
  return null;
};

// ----- Main full-screen MapView component -----
export const MapView = ({
  user,
  events,
  getDistance,
  favoriteIds = new Set(),
  applicationStatusMap = {},
  homeCoords: homeCoordsProp,
  homeRadiusMiles = 100,
  onEventsChanged,
  panelProps,
}) => {
  const navigate = useNavigate();
  const [mapReady, setMapReady] = useState(false);
  const [leafletLib, setLeafletLib] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [mapZoom, setMapZoom] = useState(4);
  const [hoveredEventId, setHoveredEventId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [localCoordsCache, setLocalCoordsCache] = useState({});
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [areaSearchLoading, setAreaSearchLoading] = useState(false);

  // Scouted (preview) events from the last Event Scout search — ghost pins
  const [scoutedEvents, setScoutedEvents] = useState(() => {
    try {
      const raw = sessionStorage.getItem('eventScoutResults');
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed?.events) ? parsed.events : [];
    } catch {
      return [];
    }
  });
  const [showScouted, setShowScouted] = useState(true);
  const [scoutCoordsCache, setScoutCoordsCache] = useState({});
  const [savingScoutId, setSavingScoutId] = useState(null);
  const [scoutSearching, setScoutSearching] = useState(false);
  const [scoutSearchArea, setScoutSearchArea] = useState('');

  // Dynamically load Leaflet
  useEffect(() => {
    const loadLeaflet = async () => {
      try {
        await import('leaflet/dist/leaflet.css');
        const L = (await import('leaflet')).default;
        const reactLeaflet = await import('react-leaflet');

        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl:
            'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
        });

        const homeIcon = L.divIcon({
          className: 'custom-home-marker',
          html: `<div style="width:32px;height:32px;border-radius:50%;background:#1E3A5F;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;">H</div>`,
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });

        setLeafletLib({ L, ...reactLeaflet, homeIcon });
        setMapReady(true);
      } catch (e) {
        console.error('Failed to load Leaflet', e);
      }
    };
    loadLeaflet();
  }, []);

  // Home coordinates: prefer precise (city-level) coords passed from Events page
  const homeCoords = useMemo(() => {
    if (homeCoordsProp?.lat && (homeCoordsProp.lng || homeCoordsProp.lon)) {
      return { lat: homeCoordsProp.lat, lng: homeCoordsProp.lng || homeCoordsProp.lon };
    }
    if (user?.home_state) return getStateCoords(user.home_state);
    return null;
  }, [homeCoordsProp, user?.home_state]);

  // Default starting view: home location + preferred radius (fallback 100 mi)
  const homeBounds = useMemo(() => {
    if (!homeCoords) return null;
    const r = homeRadiusMiles > 0 ? homeRadiusMiles : 100;
    const dLat = r / 69;
    const dLng = r / (69 * Math.cos((homeCoords.lat * Math.PI) / 180));
    return [
      [homeCoords.lat - dLat, homeCoords.lng - dLng],
      [homeCoords.lat + dLat, homeCoords.lng + dLng],
    ];
  }, [homeCoords, homeRadiusMiles]);

  const userDraggedRef = useRef(false);

  // Batch geocode events
  useEffect(() => {
    const fetchCoords = async () => {
      if (!events || events.length === 0) return;
      try {
        const locations = events.map((e) => ({
          location: e.location,
          city: e.city,
          state: e.state,
          zip: e.zip,
        }));
        const res = await geocodeAPI.batch(locations);
        const cache = {};
        (res.data || []).forEach((item) => {
          if (item.result) cache[item.address] = item.result;
        });
        setLocalCoordsCache(cache);
      } catch {
        /* non-fatal */
      }
    };
    fetchCoords();
  }, [events]);

  // Build markers list (events with coords)
  const eventsWithCoords = useMemo(() => {
    return (events || [])
      .map((event) => {
        const parts = [];
        if (event.location) parts.push(event.location);
        if (event.city) parts.push(event.city);
        if (event.state) parts.push(event.state);
        if (event.zip) parts.push(event.zip);
        const cacheKey = parts.join(', ');

        let coords = localCoordsCache[cacheKey];
        if (!coords) {
          const simpleKey = `${event.city}, ${event.state}`;
          coords = localCoordsCache[simpleKey];
        }
        if (!coords) coords = getStateCoords(event.state);
        if (!coords) return null;

        return {
          ...event,
          coords: { lat: coords.lat, lng: coords.lon || coords.lng },
          distance: getDistance(event),
        };
      })
      .filter(Boolean);
  }, [events, getDistance, localCoordsCache]);

  // Bounds covering all event pins (+ home) — used for the default view
  const pinsBounds = useMemo(() => {
    const pts = eventsWithCoords.map((e) => [e.coords.lat, e.coords.lng]);
    if (homeCoords) pts.push([homeCoords.lat, homeCoords.lng]);
    if (pts.length === 0) return null;
    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    pts.forEach(([la, ln]) => {
      if (la < minLat) minLat = la;
      if (la > maxLat) maxLat = la;
      if (ln < minLng) minLng = ln;
      if (ln > maxLng) maxLng = ln;
    });
    const padLat = Math.max((maxLat - minLat) * 0.12, 0.08);
    const padLng = Math.max((maxLng - minLng) * 0.12, 0.08);
    return [
      [minLat - padLat, minLng - padLng],
      [maxLat + padLat, maxLng + padLng],
    ];
  }, [eventsWithCoords, homeCoords]);

  // Geocode scouted preview events
  useEffect(() => {
    const fetchScoutCoords = async () => {
      if (scoutedEvents.length === 0) return;
      try {
        const locations = scoutedEvents.map((e) => ({
          location: e.location,
          city: e.city,
          state: e.state,
        }));
        const res = await geocodeAPI.batch(locations);
        const cache = {};
        (res.data || []).forEach((item) => {
          if (item.result) cache[item.address] = item.result;
        });
        setScoutCoordsCache(cache);
      } catch {
        /* non-fatal */
      }
    };
    fetchScoutCoords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoutedEvents]);

  // Scouted events with coordinates, excluding any already in the user's events
  const scoutedWithCoords = useMemo(() => {
    const existingKeys = new Set(
      (events || []).map((e) => `${(e.title || '').toLowerCase()}|${e.date || ''}`)
    );
    return scoutedEvents
      .filter((e) => !existingKeys.has(`${(e.title || '').toLowerCase()}|${e.date || ''}`))
      .map((e) => {
        const key = [e.location, e.city, e.state].filter(Boolean).join(', ');
        let coords =
          scoutCoordsCache[key] ||
          scoutCoordsCache[`${e.city}, ${e.state}`] ||
          getStateCoords(e.state);
        if (!coords) return null;
        return { ...e, coords: { lat: coords.lat, lng: coords.lon || coords.lng } };
      })
      .filter(Boolean);
  }, [scoutedEvents, scoutCoordsCache, events]);

  const removeScoutedEvent = (id) => {
    setScoutedEvents((prev) => {
      const next = prev.filter((e) => e.id !== id);
      try {
        const raw = sessionStorage.getItem('eventScoutResults');
        if (raw) {
          const parsed = JSON.parse(raw);
          parsed.events = next;
          sessionStorage.setItem('eventScoutResults', JSON.stringify(parsed));
        }
      } catch {
        /* non-fatal */
      }
      return next;
    });
  };

  const handleSaveScouted = async (event) => {
    setSavingScoutId(event.id);
    try {
      const createRes = await eventsAPI.create({
        title: event.title,
        description: event.description || '',
        date: event.date,
        end_date: event.end_date || null,
        location: event.location || '',
        city: event.city || '',
        state: event.state || '',
        event_type: event.event_type || 'other',
        booth_cost: event.booth_cost || null,
        website_url: event.website_url || null,
        event_size: event.event_size || null,
        indoor_outdoor: event.indoor_outdoor || null,
        is_community: false,
      });
      await favoritesAPI.add(createRes.data.id);
      removeScoutedEvent(event.id);
      toast.success(`Saved "${event.title}" to your events`);
      onEventsChanged?.();
    } catch {
      toast.error('Failed to save event');
    } finally {
      setSavingScoutId(null);
    }
  };

  // Apply search filter + bounds filter
  const visibleEvents = useMemo(() => {
    let list = eventsWithCoords;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (e) =>
          (e.title || '').toLowerCase().includes(q) ||
          (e.city || '').toLowerCase().includes(q) ||
          (e.state || '').toLowerCase().includes(q) ||
          (EVENT_TYPES[e.event_type]?.label || '').toLowerCase().includes(q)
      );
    }
    if (mapBounds) {
      list = list.filter((e) => {
        try {
          return mapBounds.contains([e.coords.lat, e.coords.lng]);
        } catch {
          return true;
        }
      });
    }
    return list.sort((a, b) => {
      const dA = a.distance;
      const dB = b.distance;
      if (dA != null && dB != null) return dA - dB;
      if (dA != null) return -1;
      if (dB != null) return 1;
      return (a.date || '').localeCompare(b.date || '');
    });
  }, [eventsWithCoords, mapBounds, searchQuery]);

  // Type counts for the legend (based on current view)
  const typeCounts = useMemo(() => {
    const counts = {};
    visibleEvents.forEach((e) => {
      const t = e.event_type || 'other';
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [visibleEvents]);

  // Group events into zoom-aware clusters. Grid cell shrinks as the user
  // zooms in, so clusters split apart into finer detail with each zoom step.
  const markerGroups = useMemo(() => {
    const cell = 56.25 / Math.pow(2, mapZoom); // ~40px cluster radius at any zoom
    const groups = {};
    eventsWithCoords.forEach((e) => {
      const key = `${Math.floor(e.coords.lat / cell)},${Math.floor(e.coords.lng / cell)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(e);
    });
    return Object.entries(groups).map(([key, evts]) => {
      const lat = evts.reduce((s, e) => s + e.coords.lat, 0) / evts.length;
      const lng = evts.reduce((s, e) => s + e.coords.lng, 0) / evts.length;
      const distinctCoords = new Set(evts.map((e) => `${e.coords.lat},${e.coords.lng}`)).size;
      return { key, events: evts, lat, lng, distinctCoords };
    });
  }, [eventsWithCoords, mapZoom]);

  // Memoized icon factory — stable icon instances prevent react-leaflet from
  // rebinding every marker (and its open popup) on each render, which caused
  // an autopan → moveend → setState → re-render infinite loop.
  const icons = useMemo(() => {
    if (!leafletLib) return null;
    const { L } = leafletLib;
    const cache = new Map();
    const cached = (key, make) => {
      if (!cache.has(key)) cache.set(key, make());
      return cache.get(key);
    };

    const pin = (typeKey, highlighted = false) =>
      cached(`pin:${typeKey}:${highlighted}`, () => {
        const color = colorFor(typeKey);
        const IconComp = iconFor(typeKey);
        const svg = renderToString(<IconComp size={12} color="white" strokeWidth={2.5} />);
        const size = highlighted ? 38 : 30;
        const ring = highlighted
          ? 'box-shadow:0 0 0 4px rgba(30,58,95,0.35),0 2px 6px rgba(0,0,0,0.35);'
          : 'box-shadow:0 2px 6px rgba(0,0,0,0.35);';
        return L.divIcon({
          className: 'custom-event-marker',
          html: `<div style="width:${size}px;height:${size}px;"><div style="width:100%;height:100%;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2.5px solid white;${ring}display:flex;align-items:center;justify-content:center;"><span style="transform:rotate(45deg);display:flex;align-items:center;justify-content:center;">${svg}</span></div></div>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size],
          popupAnchor: [0, -size],
        });
      });

    const cluster = (count, color, highlighted = false) =>
      cached(`cluster:${count}:${color}:${highlighted}`, () => {
        const size = highlighted ? 42 : 36;
        const ring = highlighted
          ? 'box-shadow:0 0 0 4px rgba(30,58,95,0.35),0 2px 8px rgba(0,0,0,0.35);'
          : 'box-shadow:0 2px 8px rgba(0,0,0,0.35);';
        return L.divIcon({
          className: 'custom-cluster-marker',
          html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:3px solid white;${ring}display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:13px;font-family:'Outfit',sans-serif;">${count}</div>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
          popupAnchor: [0, -size / 2],
        });
      });

    const ghost = (typeKey) =>
      cached(`ghost:${typeKey}`, () => {
        const color = colorFor(typeKey);
        const svg = renderToString(<Sparkles size={11} color="white" strokeWidth={2.5} />);
        return L.divIcon({
          className: 'custom-ghost-marker',
          html: `<div style="width:28px;height:28px;border-radius:50%;background:${color};opacity:0.65;border:2px dashed white;box-shadow:0 2px 6px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;">${svg}</div>`,
          iconSize: [28, 28],
          iconAnchor: [14, 14],
          popupAnchor: [0, -14],
        });
      });

    return { pin, cluster, ghost };
  }, [leafletLib]);

  if (!mapReady || !leafletLib) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-[#FDF9F4]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#1E3A5F] animate-spin mx-auto mb-2" />
          <p className="text-[#4A4A4A]">Loading map...</p>
        </div>
      </div>
    );
  }

  const { MapContainer, TileLayer, Marker, Popup, Tooltip: LeafletTooltip, useMapEvents, useMap, homeIcon } = leafletLib;

  const SCOUT_DEFAULT_QUERY = 'craft fairs, festivals, artisan markets and vendor events';

  const handleSearchThisArea = async () => {
    if (scoutSearching) return;
    setAreaSearchLoading(true);
    let locationStr, radiusMiles;
    try {
      let center;
      if (mapBounds) {
        const c = mapBounds.getCenter();
        center = { lat: c.lat, lng: c.lng };
        radiusMiles = Math.round(Math.abs(mapBounds.getNorthEast().lat - c.lat) * 69);
      } else if (homeCoords) {
        center = homeCoords;
        radiusMiles = homeRadiusMiles;
      } else {
        toast.error('Pan the map to an area first');
        return;
      }
      radiusMiles = Math.min(Math.max(radiusMiles, 10), 500);
      const res = await geocodeAPI.reverse(center.lat, center.lng);
      const { city, state } = res.data || {};
      if (!city && !state) {
        toast.error('Could not identify this area — try zooming in closer');
        return;
      }
      locationStr = [city, state].filter(Boolean).join(', ');
    } catch {
      toast.error('Could not identify this area');
      return;
    } finally {
      setAreaSearchLoading(false);
    }

    // Run Event Scout directly and plot findings as ghost pins
    setScoutSearchArea(locationStr);
    setScoutSearching(true);
    try {
      const res = await eventScoutAPI.search({
        query: SCOUT_DEFAULT_QUERY,
        location: locationStr,
        max_distance: radiusMiles,
        min_confidence: 0,
      });
      const found = res.data?.events || [];
      // Mirror results to sessionStorage so the Event Scout page shows them too
      try {
        sessionStorage.setItem('eventScoutResults', JSON.stringify(res.data));
        sessionStorage.setItem('eventScoutQuery', SCOUT_DEFAULT_QUERY);
        sessionStorage.setItem('eventScoutLocation', locationStr);
      } catch {
        /* non-fatal */
      }
      setScoutedEvents(found);
      setShowScouted(true);
      if (found.length > 0) {
        toast.success(
          `Event Scout found ${found.length} opportunit${found.length === 1 ? 'y' : 'ies'} near ${locationStr} — shown as preview pins`
        );
      } else {
        toast.info(`No new events found near ${locationStr}. Try a wider area.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Event Scout search failed. Please try again.');
    } finally {
      setScoutSearching(false);
    }
  };

  const makePinIcon = icons.pin;
  const makeClusterIcon = icons.cluster;
  const makeGhostIcon = icons.ghost;

  const legendTypes = Object.keys(EVENT_TYPES).filter((key) => (typeCounts[key] || 0) > 0);

  return (
    <div className="relative h-full w-full flex overflow-hidden bg-[#EDF1F5]" data-testid="map-fullscreen">
      {!panelCollapsed && (
        <MapSidePanel
          user={user}
          visibleEvents={visibleEvents}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          hoveredEventId={hoveredEventId}
          setHoveredEventId={setHoveredEventId}
          onCollapse={() => setPanelCollapsed(true)}
          favoriteIds={favoriteIds}
          applicationStatusMap={applicationStatusMap}
          panelProps={panelProps}
        />
      )}

      {/* Map area */}
      <div className="flex-1 relative h-full" data-testid="map-view-container">
        <MapContainer
          bounds={homeBounds || USA_DEFAULT_BOUNDS}
          center={USA_CENTER}
          zoom={4}
          zoomSnap={0.2}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <BoundsTracker
            useMapEventsHook={useMapEvents}
            onChange={(b, z) => {
              setMapBounds((prev) =>
                prev && prev.toBBoxString() === b.toBBoxString() ? prev : b
              );
              setMapZoom((prev) => (prev === z ? prev : z));
            }}
            onUserDrag={() => {
              userDraggedRef.current = true;
            }}
          />
          <InitialViewController
            homeBounds={homeBounds}
            pinsBounds={pinsBounds}
            hasPins={eventsWithCoords.length > 0}
            useMapHook={useMap}
            userDraggedRef={userDraggedRef}
          />

          {homeCoords && (
            <Marker position={[homeCoords.lat, homeCoords.lng]} icon={homeIcon}>
              <Popup>
                <div className="text-center">
                  <p className="font-bold text-[#1E3A5F]">Your Home</p>
                  <p className="text-sm text-[#4A4A4A]">
                    {[user.home_city, user.home_state].filter(Boolean).join(', ')}
                  </p>
                </div>
              </Popup>
            </Marker>
          )}

          {markerGroups.map((group) => {
            if (group.events.length === 1) {
              const event = group.events[0];
              return (
                <Marker
                  key={event.id}
                  position={[group.lat, group.lng]}
                  icon={makePinIcon(event.event_type, hoveredEventId === event.id)}
                  eventHandlers={{
                    mouseover: () => setHoveredEventId(event.id),
                    mouseout: () => setHoveredEventId(null),
                  }}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <p className="font-bold text-[#1E3A5F] mb-1">{event.title}</p>
                      <p className="text-xs text-[#4A4A4A] mb-2">
                        {[event.city, event.state].filter(Boolean).join(', ')}
                      </p>
                      <div className="flex items-center gap-2 text-xs mb-1">
                        <CalendarDays className="w-3 h-3 text-[#FE5F55]" />
                        <span>{formatDate(event.date)}</span>
                      </div>
                      {event.booth_cost != null && (
                        <div className="flex items-center gap-2 text-xs mb-1">
                          <DollarSign className="w-3 h-3 text-[#1E3A5F]" />
                          <span>{formatCurrency(event.booth_cost)}</span>
                        </div>
                      )}
                      {event.distance != null && !isNaN(event.distance) && (
                        <div className="flex items-center gap-2 text-xs mb-2">
                          <Navigation className="w-3 h-3 text-[#1E3A5F]" />
                          <span>{event.distance} miles from home</span>
                        </div>
                      )}
                      <Link to={`/events/${event.id}`}>
                        <Button size="sm" className="w-full btn-primary mt-1 h-7 text-xs">
                          View Details
                        </Button>
                      </Link>
                    </div>
                  </Popup>
                  {mapZoom >= STREET_LABEL_ZOOM && (
                    <LeafletTooltip
                      key="street-label"
                      permanent
                      direction="bottom"
                      offset={[0, 4]}
                      opacity={1}
                      className="street-label"
                    >
                      <div>
                        <span className="font-semibold">{event.title}</span>
                        {(event.location || event.city) && (
                          <>
                            <br />
                            <span className="text-[10px] text-[#71717A]">
                              {event.location || [event.city, event.state].filter(Boolean).join(', ')}
                            </span>
                          </>
                        )}
                      </div>
                    </LeafletTooltip>
                  )}
                </Marker>
              );
            }

            // Cluster marker: color of the dominant event type in the group
            const groupTypeCounts = {};
            group.events.forEach((e) => {
              const t = e.event_type || 'other';
              groupTypeCounts[t] = (groupTypeCounts[t] || 0) + 1;
            });
            const dominantType = Object.entries(groupTypeCounts).sort((a, b) => b[1] - a[1])[0][0];
            const isGroupHovered = group.events.some((e) => e.id === hoveredEventId);

            return (
              <Marker
                key={group.key}
                position={[group.lat, group.lng]}
                icon={makeClusterIcon(group.events.length, colorFor(dominantType), isGroupHovered)}
                eventHandlers={{
                  click: (e) => {
                    // Zoom straight to a street-level close-up covering all
                    // event addresses under this pin
                    const map = e.target._map;
                    if (group.distinctCoords > 1) {
                      const b = leafletLib.L.latLngBounds(
                        group.events.map((ev) => [ev.coords.lat, ev.coords.lng])
                      );
                      map.flyToBounds(b.pad(0.3), { maxZoom: 16 });
                    } else {
                      map.flyTo([group.lat, group.lng], 16);
                    }
                  },
                }}
              >
                {/* Hover summary of the events under this pin; pinned open at street zoom */}
                <LeafletTooltip
                  key={mapZoom >= STREET_LABEL_ZOOM ? 'cluster-permanent' : 'cluster-hover'}
                  permanent={mapZoom >= STREET_LABEL_ZOOM}
                  direction="top"
                  offset={[0, -14]}
                  opacity={1}
                  className={mapZoom >= STREET_LABEL_ZOOM ? 'street-label' : undefined}
                >
                  <div className="min-w-[210px] max-w-[260px]">
                    <p className="font-bold text-[#1E3A5F] mb-1.5 text-xs">
                      {group.events.length} events at this location
                    </p>
                    <div className="space-y-1">
                      {group.events.slice(0, 8).map((e) => (
                        <div key={e.id} className="flex items-start gap-1.5 text-[11px]">
                          <span
                            className="inline-block w-2 h-2 rounded-full mt-1 flex-shrink-0"
                            style={{ backgroundColor: colorFor(e.event_type) }}
                          />
                          <span className="whitespace-normal">
                            <span className="font-semibold text-[#1E3A5F]">{e.title}</span>
                            <span className="text-[#71717A]"> · {formatDate(e.date)}</span>
                          </span>
                        </div>
                      ))}
                      {group.events.length > 8 && (
                        <p className="text-[10px] text-[#71717A] italic">
                          +{group.events.length - 8} more…
                        </p>
                      )}
                    </div>
                    <p className="text-[10px] text-[#71717A] mt-1.5 italic">
                      Click to zoom in for a close-up view
                    </p>
                  </div>
                </LeafletTooltip>
              </Marker>
            );
          })}

          {/* Ghost/preview pins for scouted opportunities */}
          {showScouted &&
            scoutedWithCoords.map((event) => (
              <Marker
                key={`scout-${event.id}`}
                position={[event.coords.lat, event.coords.lng]}
                icon={makeGhostIcon(event.event_type)}
              >
                <Popup>
                  <div className="min-w-[220px]">
                    <div className="flex items-center gap-1 mb-1">
                      <Sparkles className="w-3 h-3 text-purple-600" />
                      <span className="text-[10px] font-bold uppercase tracking-widest text-purple-600">
                        Scouted Preview
                      </span>
                      {event.confidence_score != null && (
                        <span className="ml-auto text-[10px] font-semibold text-[#71717A]">
                          {event.confidence_score}% match
                        </span>
                      )}
                    </div>
                    <p className="font-bold text-[#1E3A5F] mb-1">{event.title}</p>
                    <p className="text-xs text-[#4A4A4A] mb-1">
                      {[event.city, event.state].filter(Boolean).join(', ')}
                    </p>
                    {event.date && (
                      <div className="flex items-center gap-2 text-xs mb-1">
                        <CalendarDays className="w-3 h-3 text-[#FE5F55]" />
                        <span>{formatDate(event.date)}</span>
                      </div>
                    )}
                    {event.booth_cost != null && (
                      <div className="flex items-center gap-2 text-xs mb-1">
                        <DollarSign className="w-3 h-3 text-[#1E3A5F]" />
                        <span>{formatCurrency(event.booth_cost)}</span>
                      </div>
                    )}
                    {event.source_name && (
                      <div className="flex items-center gap-2 text-[11px] text-[#71717A] mb-2">
                        <ExternalLink className="w-3 h-3" />
                        <span>via {event.source_name}</span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-1.5 mt-1">
                      <Button
                        size="sm"
                        onClick={() => handleSaveScouted(event)}
                        disabled={savingScoutId === event.id}
                        className="bg-[#1E3A5F] hover:bg-[#152a47] text-white h-7 text-xs"
                        data-testid={`ghost-save-${event.id}`}
                      >
                        {savingScoutId === event.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          'Save Event'
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/ai-discover')}
                        className="border-purple-400 text-purple-700 hover:bg-purple-50 h-7 text-xs"
                        data-testid={`ghost-review-${event.id}`}
                      >
                        Review in Scout
                      </Button>
                    </div>
                  </div>
                </Popup>
                {mapZoom >= STREET_LABEL_ZOOM && (
                  <LeafletTooltip
                    key="street-label"
                    permanent
                    direction="bottom"
                    offset={[0, 10]}
                    opacity={1}
                    className="street-label street-label-scouted"
                  >
                    <div>
                      <span className="font-semibold">{event.title}</span>
                      {(event.location || event.city) && (
                        <>
                          <br />
                          <span className="text-[10px] text-[#71717A]">
                            {event.location || [event.city, event.state].filter(Boolean).join(', ')}
                          </span>
                        </>
                      )}
                    </div>
                  </LeafletTooltip>
                )}
              </Marker>
            ))}
        </MapContainer>

        {/* Search this area (Event Scout) */}
        <button
          onClick={handleSearchThisArea}
          disabled={areaSearchLoading || scoutSearching}
          className="absolute top-3 left-1/2 -translate-x-1/2 z-[600] bg-[#1E3A5F] hover:bg-[#152a47] text-white shadow-lg rounded-full px-4 py-2 flex items-center gap-2 text-xs font-semibold transition-colors disabled:opacity-70"
          data-testid="map-search-this-area"
        >
          {areaSearchLoading || scoutSearching ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          {scoutSearching ? 'Scouting…' : 'Search this area'}
        </button>

        {/* Scout searching indicator */}
        {scoutSearching && (
          <div
            className="absolute top-14 left-1/2 -translate-x-1/2 z-[650] bg-white/95 backdrop-blur rounded-xl shadow-xl border border-purple-200 px-5 py-3 flex items-center gap-3 max-w-[90%]"
            data-testid="map-scout-searching"
          >
            <div className="relative flex-shrink-0">
              <Sparkles className="w-6 h-6 text-purple-600 animate-pulse" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#1E3A5F] truncate">
                Event Scout is searching {scoutSearchArea}…
              </p>
              <p className="text-[11px] text-[#71717A]">
                Scanning Eventbrite, FestivalNet &amp; more — findings will appear as preview pins on the map
              </p>
            </div>
            <Loader2 className="w-4 h-4 text-purple-600 animate-spin flex-shrink-0" />
          </div>
        )}

        {/* Scouted opportunities toggle */}
        {!scoutSearching && scoutedWithCoords.length > 0 && (
          <button
            onClick={() => setShowScouted(!showScouted)}
            className={`absolute top-14 left-1/2 -translate-x-1/2 z-[600] shadow-lg rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs font-semibold transition-colors border-2 border-dashed ${
              showScouted
                ? 'bg-purple-50 border-purple-400 text-purple-700 hover:bg-purple-100'
                : 'bg-white border-gray-300 text-[#71717A] hover:bg-gray-50'
            }`}
            data-testid="map-scouted-toggle"
            title={showScouted ? 'Hide scouted previews' : 'Show scouted previews'}
          >
            <Sparkles className="w-3.5 h-3.5" />
            {scoutedWithCoords.length} scouted opportunit{scoutedWithCoords.length === 1 ? 'y' : 'ies'}
            {showScouted ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          </button>
        )}

        {/* Expand panel button (when collapsed) */}
        {panelCollapsed && (
          <button
            onClick={() => setPanelCollapsed(false)}
            className="absolute top-3 left-3 z-[600] bg-white shadow-lg border border-gray-200 rounded-full px-3 py-2 flex items-center gap-1.5 text-xs font-semibold text-[#1E3A5F] hover:bg-[#FFEDDF] transition-colors"
            data-testid="map-panel-expand"
          >
            <ChevronRight className="w-4 h-4" />
            {visibleEvents.length} events in view
          </button>
        )}

        {/* Total events badge (top-right) */}
        <div
          className="absolute top-3 right-3 z-[500] bg-white/95 backdrop-blur px-3 py-1.5 rounded-full shadow-lg border border-gray-200 text-xs font-semibold text-[#1E3A5F]"
          data-testid="map-visible-count"
        >
          {eventsWithCoords.length} event{eventsWithCoords.length !== 1 ? 's' : ''} shown
        </div>

        {/* Types legend (bottom-left) */}
        <div
          className="absolute bottom-6 left-3 z-[500] bg-white/95 backdrop-blur rounded-xl shadow-lg border border-gray-200 px-4 py-3 max-w-[220px]"
          data-testid="map-types-legend"
        >
          <p className="text-[10px] font-bold text-[#71717A] uppercase tracking-widest mb-2">
            Types
          </p>
          {legendTypes.length === 0 ? (
            <p className="text-xs text-[#71717A]">No events in view</p>
          ) : (
            <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
              {legendTypes.map((key) => (
                <div key={key} className="flex items-center gap-2 text-xs" data-testid={`legend-${key}`}>
                  <span
                    className="inline-block w-3 h-3 rounded-full border border-white shadow flex-shrink-0"
                    style={{ backgroundColor: colorFor(key) }}
                  />
                  <span className="text-[#4A4A4A]">{labelFor(key)}</span>
                  <span className="ml-auto font-semibold text-[#1E3A5F]">{typeCounts[key]}</span>
                </div>
              ))}
            </div>
          )}
          {showScouted && scoutedWithCoords.length > 0 && (
            <div className="flex items-center gap-2 text-xs mt-2 pt-2 border-t border-gray-100" data-testid="legend-scouted">
              <span className="inline-block w-3 h-3 rounded-full border-2 border-dashed border-purple-500 bg-purple-200 flex-shrink-0" />
              <span className="text-[#4A4A4A]">Scouted (preview)</span>
              <span className="ml-auto font-semibold text-purple-600">{scoutedWithCoords.length}</span>
            </div>
          )}
          {homeCoords && (
            <div className="flex items-center gap-2 text-xs mt-2 pt-2 border-t border-gray-100">
              <span className="inline-block w-3 h-3 rounded-full bg-[#1E3A5F] border border-white shadow flex-shrink-0" />
              <span className="text-[#4A4A4A]">Your Home</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapView;

